import { useEffect, useState } from "react";
import mealsApi from "../../api/meals";
import Swal from "sweetalert2";

interface SwapMealDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order: any | null;
  onSwap: (order: any, meal: any) => Promise<void>;
}

interface Meal {
  _id: string;
  name: string;
  description: string;
  nutritionalInfo: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    fibre?: number;
    magnesium?: number;
    vitaminsAndMinerals?: string[];
  };
  allergens: string[];
  ingredients: string[];
  portionSize: string;
  category: string;
  serialNumber: string;
  imageUrl: string;
  rating: number;
  noOfRatings: number;
}

const getSerialNumberAsNumber = (serialNumber: string): number => {
  if (!serialNumber) return 0;
  const num = parseInt(serialNumber.replace(/\D/g, ""), 10);
  return isNaN(num) ? 0 : num;
};

const SwapMealDialog = ({
  isOpen,
  onClose,
  order,
  onSwap,
}: SwapMealDialogProps) => {
  const [meals, setMeals] = useState<any[]>([]);
  const [selectedMealId, setSelectedMealId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    if (!isOpen) return;
    const fetchMeals = async () => {
      try {
        setFetching(true);
        const data = await mealsApi.getMeals({
          limit: 200,
          sortBy: "serialNumber",
          sortOrder: "asc",
        });

        const sortedMeals = (data.meals || [])
          .slice()
          .sort((a: Meal, b: Meal) => {
            const aNum = getSerialNumberAsNumber(a.serialNumber);
            const bNum = getSerialNumberAsNumber(b.serialNumber);
            return aNum - bNum;
          });

        setMeals(sortedMeals);

        // setMeals(meals || []);
      } catch (e) {
        console.error("Failed to fetch meals", e);
        Swal.fire({
          title: "Error",
          text: "Failed to load meals",
          icon: "error",
        });
      } finally {
        setFetching(false);
      }
    };
    fetchMeals();
  }, [isOpen]);

  useEffect(() => {
    if (order?.mealId?._id) {
      setSelectedMealId(order.mealId._id);
    } else {
      setSelectedMealId("");
    }
  }, [order]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!selectedMealId || !order) return;
    if (selectedMealId === order?.mealId?._id) {
      Swal.fire({
        title: "No change",
        text: "Please select a different meal",
        icon: "info",
      });
      return;
    }
    try {
      setLoading(true);
      const newMeal = meals.find((m) => m._id === selectedMealId);
      await onSwap(order, newMeal);
      onClose();
    } catch (e) {
      // onSwap handles errors
    } finally {
      setLoading(false);
    }
  };

  const filteredMeals = meals.filter((meal: Meal) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      meal.name?.toLowerCase().includes(q) ||
      (meal.serialNumber || "").toLowerCase().includes(q)
    );
  });

  const selectedMeal = meals.find((m: Meal) => m._id === selectedMealId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 px-3">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Swap Meal</h3>
          <p className="text-sm text-gray-500 mt-1">
            {order ? `Order for ${order?.userId?.name || "User"}` : ""}
          </p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Meal
            </label>
            <div className="text-sm text-gray-900">
              {order?.mealId?.name || "N/A"}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select New Meal
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                disabled={fetching}
                className="w-full flex justify-between items-center px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <span className="truncate text-left">
                  {selectedMeal ? (
                    <>
                      {selectedMeal.serialNumber
                        ? `${selectedMeal.serialNumber}. `
                        : ""}
                      {selectedMeal.name}
                    </>
                  ) : (
                    "Select a meal"
                  )}
                </span>
                <svg
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {dropdownOpen && (
                <div className="absolute z-10 mt-2 left-0 right-0 rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search meals..."
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="max-h-56 overflow-auto">
                    {filteredMeals.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        No results
                      </div>
                    ) : (
                      filteredMeals.map((meal: Meal) => (
                        <button
                          key={meal._id}
                          type="button"
                          onClick={() => {
                            setSelectedMealId(meal._id);
                            setDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 ${
                            selectedMealId === meal._id ? "bg-indigo-50" : ""
                          }`}
                        >
                          <span className="font-medium">
                            {meal.serialNumber ? `${meal.serialNumber}. ` : ""}
                            {meal.name}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={
              loading ||
              fetching ||
              !selectedMealId ||
              selectedMealId === order?.mealId?._id
            }
            className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Swapping..." : "Swap Meal"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwapMealDialog;
